import { Space, Flex, Divider, Dropdown, Tooltip, Row, Col, Switch, Modal, Button } from "antd"
import { Text, Title } from "ui/Typography"
import styles from "./index.module.css"
import { useTranslation } from "react-i18next"
import MoreActionEllipsis from "ui/MoreActionEllipsis"
import PropTypes from "prop-types"
import { displayPaymentStatus, BOOKING_REQUEST_TYPES } from "../../utils"
import StatusTag from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/StatusTag"
import { useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { getAutoCollectWindowStatus, patchBookingPaymentOption, getClientBookingDetails } from "store/bookings"
import AdjustBookingPriceModal from "./Modals/AdjustBookingPrice"
import { Icon } from "ui/Icon"
import AddOnPhoto from "ui/AddOnPhoto"
import { numberToCurrency } from "helpers/string"
import useSummaryDetails from "./hooks/useSummaryDetails"
import { spacings } from "themes/variables"
import SummaryKeyValue from "./SummaryKeyValue"
import TakePaymentModal from "./Modals/TakePayment"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import { BOOKING_SOURCE } from "pagesV2/pro/Bookings/constants"
import dayjs from "dayjs"
import { CloseOutlined } from "@ant-design/icons"
import warningIcon from "assets/icons/warningIcon.svg"

const PaymentSummary = ({
  paymentSummary,
  customFees,
  bookingAddOns,
  payment_status,
  hasPayments,
  bookingUUID,
  bookingId,
  bookingStatus,
  pricing,
  bookingTitle,
  bookedOn,
  clientName,
  clientEmail,
  bookingSource,
  experienceAvatar,
  bookedDate,
  startTime,
  is_chargeable,
  platformFeeSetting
}) => {
  const isMarketplaceBooking = bookingSource === BOOKING_SOURCE.marketplace || bookingSource === BOOKING_SOURCE.website
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { hasPermission } = usePermissions()
  const [isTakePaymentModalOpen, setIsTakePaymentModalOpen] = useState(false)
  const [isAdjustBookingPriceModalOpen, setIsAdjustBookingPriceModalOpen] = useState(false)
  const [isAutoCollectToggling, setIsAutoCollectToggling] = useState(false)
  const [isWithin24hModalOpen, setIsWithin24hModalOpen] = useState(false)
  const [pendingAutoCollectValue, setPendingAutoCollectValue] = useState(null)
  const isDepositOption = paymentSummary?.payment_option?.includes("deposit")
  const autoCollectEnabled =
    paymentSummary?.payment_option === "deposit_by_link_and_balance_later" || paymentSummary?.payment_option === "deposit_and_balance_later"
  const isLinkPayment = paymentSummary?.payment_option?.includes("by_link")

  const applyAutoCollect = async (value) => {
    setIsAutoCollectToggling(true)
    try {
      const option = value
        ? isLinkPayment
          ? "deposit_by_link_and_balance_later"
          : "deposit_and_balance_later"
        : isLinkPayment
          ? "deposit_by_link_and_balance_in_cash"
          : "deposit_and_balance_in_cash"
      await dispatch(patchBookingPaymentOption(bookingUUID, option))
      await dispatch(getClientBookingDetails(bookingUUID))
    } finally {
      setIsAutoCollectToggling(false)
    }
  }

  const handleAutoCollectChange = async (value) => {
    setIsAutoCollectToggling(true)
    const status = await dispatch(getAutoCollectWindowStatus(bookingId))
    setIsAutoCollectToggling(false)
    if (status?.within_window) {
      setPendingAutoCollectValue(value)
      setIsWithin24hModalOpen(true)
      return
    }
    await applyAutoCollect(value)
  }

  const handleWithin24hConfirm = async () => {
    setIsWithin24hModalOpen(false)
    await applyAutoCollect(pendingAutoCollectValue)
    setPendingAutoCollectValue(null)
  }

  const handleWithin24hCancel = () => setIsWithin24hModalOpen(false)

  const autoCollectDateTime = useMemo(() => {
    if (!bookedDate || !startTime) return null
    const dt = dayjs(`${bookedDate} ${startTime}`, "YYYY-MM-DD h:mm A").subtract(24, "hours")
    return dt.isValid() ? dt.format("MMMM D, YYYY [at] h:mm A") : null
  }, [bookedDate, startTime])

  const summaryDetails = useSummaryDetails(paymentSummary, customFees, { combineFees: true, mergeAllFeesToTax: true })
  const discountAppliesToAddOns = paymentSummary?.applicability_rules?.apply_to_add_ons ?? paymentSummary?.apply_to_add_ons ?? false
  const taxFeesIndex = summaryDetails.findIndex((d) => d?.combinedFee || d?.labelKey === "client.payment.labels.taxes_fees")
  const beforeTaxAll = taxFeesIndex >= 0 ? summaryDetails.slice(0, taxFeesIndex) : summaryDetails
  const taxAndAfterDetails = taxFeesIndex >= 0 ? summaryDetails.slice(taxFeesIndex) : []
  const discountIndex = beforeTaxAll.findIndex((d) => d?.isDiscount)
  const discountDetails = discountIndex >= 0 ? [beforeTaxAll[discountIndex]] : []
  const preDiscountDetails = discountIndex >= 0 ? beforeTaxAll.slice(0, discountIndex) : beforeTaxAll
  const postDiscountDetails = discountIndex >= 0 ? beforeTaxAll.slice(discountIndex + 1) : []

  // Split preDiscountDetails at the subtotal line so add-ons render between pricing and subtotal
  const subtotalIndex = preDiscountDetails.findIndex((d) => d.isSubtotal)
  const pricingLines = subtotalIndex >= 0 ? preDiscountDetails.slice(0, subtotalIndex) : preDiscountDetails
  const subtotalLine = subtotalIndex >= 0 ? [preDiscountDetails[subtotalIndex]] : []
  const postSubtotalLines = subtotalIndex >= 0 ? preDiscountDetails.slice(subtotalIndex + 1) : []
  const hasAddOns = bookingAddOns?.length > 0
  const { total_price, remaining_balance, adjust_price_history } = paymentSummary ?? {}
  const canEditBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.EDIT)

  const handleCloseTakePaymentModal = () => setIsTakePaymentModalOpen(false)
  const handleCloseAdjustBookingPriceModal = () => setIsAdjustBookingPriceModalOpen(false)

  const payTitle = hasPayments ? t("client.client_booking.total") : t("client.client_booking.total_to_pay")
  const isUpcomingOrCompleted =
    bookingStatus?.toLowerCase() === BOOKING_REQUEST_TYPES.UPCOMING || bookingStatus?.toLowerCase() === BOOKING_REQUEST_TYPES.COMPLETED

  const dropdownItems = useMemo(() => {
    const items = [
      {
        key: "take_payment",
        label: (
          <Tooltip title={remaining_balance === 0 ? "No remaining balance to collect" : undefined}>
            <span>Collect Payment</span>
          </Tooltip>
        ),
        onClick: () => setIsTakePaymentModalOpen(true),
        disabled: remaining_balance === 0
      }
    ]
    if (canEditBooking) {
      items.push({
        key: "adjust_booking_price",
        label: "Adjust Booking Price",
        onClick: () => setIsAdjustBookingPriceModalOpen(true),
        disabled: false
      })
    }

    return items
  }, [paymentSummary?.payment_option, canEditBooking, remaining_balance])

  const remainingBalValue = numberToCurrency(remaining_balance)

  return (
    <>
      <Space direction="vertical" className={`${styles.paymentSummaryContainer} w-full p-xl`}>
        <Flex gap={4} justify="space-between" className="mb-l" align="center">
          <Flex gap={4} vertical>
            <Title level={4}>{t("client.payment.labels.payment_summary")}</Title>
            {payment_status && <StatusTag status={displayPaymentStatus(payment_status)} bookingStatus={bookingStatus} />}
          </Flex>
          {isUpcomingOrCompleted && (
            <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items: dropdownItems }}>
              <MoreActionEllipsis />
            </Dropdown>
          )}
        </Flex>
        <Flex vertical gap={spacings.XS}>
          <SummaryKeyValue details={pricingLines} valueAlign="right" />
          {hasAddOns && (
            <>
              <Divider size="small" className="m-0" />
              <Text fw={600}>{t("booking.add_ons.title", { defaultValue: "Add-ons" })}</Text>
              {bookingAddOns.map((a) => (
                <Row key={a.id} align="middle" gutter={8}>
                  <Col xs={14} md={12}>
                    <Flex align="center" gap={spacings.XS} className={styles.addOnTitleCell}>
                      <AddOnPhoto src={a.photo_url} alt={a.title} size={32} iconSize={16} />
                      <Flex vertical className={styles.addOnTitleCell}>
                        <Tooltip title={a.title} placement="topLeft">
                          <Text fw={500} className={styles.addOnTitle}>{a.title}</Text>
                        </Tooltip>
                        <Text type="secondary" className={styles.addOnSubtitle}>
                          {`${a.quantity} × ${numberToCurrency(Number(a.unit_price))}`}
                        </Text>
                      </Flex>
                    </Flex>
                  </Col>
                  <Col xs={10} md={12} className={styles.alignRight}>
                    <Text fw={500}>{numberToCurrency(Number(a.total_price))}</Text>
                  </Col>
                </Row>
              ))}
            </>
          )}
          {hasAddOns && <Divider size="small" className="m-0" />}
          {subtotalLine.length > 0 && <SummaryKeyValue details={subtotalLine} valueAlign="right" />}
          {postSubtotalLines.length > 0 && <SummaryKeyValue details={postSubtotalLines} valueAlign="right" />}
          {!discountAppliesToAddOns && discountDetails.length > 0 && <SummaryKeyValue details={discountDetails} valueAlign="right" />}
          {discountAppliesToAddOns && discountDetails.length > 0 && <SummaryKeyValue details={discountDetails} valueAlign="right" />}
          {postDiscountDetails.length > 0 && <SummaryKeyValue details={postDiscountDetails} valueAlign="right" />}
          {taxAndAfterDetails.length > 0 && <SummaryKeyValue details={taxAndAfterDetails} valueAlign="right" />}
          <Divider size="small" className="m-0" />
          {adjust_price_history?.map((item, idx) => {
            const { price_difference, reason } = item || {}
            return (
              <Row key={item?.id + idx}>
                <Col xs={14} md={12}>
                  <Text>
                    Price Change{" "}
                    <Tooltip title={reason}>
                      <Icon type="feather" name="Info" width={16} height={16} />
                    </Tooltip>
                  </Text>
                </Col>
                <Col xs={10} md={12} style={{ textAlign: "right" }}>
                  <Text fw={500}>
                    {price_difference > 0 ? "+" : ""}
                    {numberToCurrency(price_difference)}
                  </Text>
                </Col>
              </Row>
            )
          })}
          <Row>
            <Col xs={14} md={12}>
              <Text fw={hasPayments ? 400 : 700}>{payTitle}</Text>
            </Col>
            <Col xs={10} md={12} style={{ textAlign: "right" }}>
              <Text fw={hasPayments ? 400 : 700}>{numberToCurrency(total_price)}</Text>
            </Col>
          </Row>
          {hasPayments && (
            <Row>
              <Col xs={14} md={12}>
                <Text fw={700}>{t("client.client_booking.remaining_balance")}</Text>
              </Col>
              <Col xs={10} md={12} style={{ textAlign: "right" }}>
                <Text fw={700}>{remainingBalValue}</Text>
              </Col>
            </Row>
          )}
          {isDepositOption && remaining_balance > 0 && is_chargeable && payment_status !== "unpaid" && (
            <Row align="middle" className={styles.autoCollectRow}>
              <Col flex="auto">
                <Flex gap={4} align="center">
                  <Text fw={500}>{t("client.client_booking.auto_collect_balance")}</Text>
                  <Tooltip title={t("client.client_booking.auto_collect_info")}>
                    <Icon type="feather" name="Info" width={16} height={16} />
                  </Tooltip>
                </Flex>
                {autoCollectEnabled && autoCollectDateTime && (
                  <Text style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                    {t("client.client_booking.auto_collect_schedule", { dateTime: autoCollectDateTime })}
                  </Text>
                )}
              </Col>
              <Col>
                <Switch
                  checked={autoCollectEnabled}
                  onChange={handleAutoCollectChange}
                  loading={isAutoCollectToggling}
                  disabled={isMarketplaceBooking}
                />
              </Col>
            </Row>
          )}
        </Flex>
      </Space>
      {isTakePaymentModalOpen && (
        <TakePaymentModal
          remainingBalance={hasPayments ? remaining_balance : total_price}
          bookingUUID={bookingUUID}
          bookingId={bookingId}
          onOpen={isTakePaymentModalOpen}
          onClose={handleCloseTakePaymentModal}
          bookingTitle={bookingTitle}
          bookedOn={bookedOn}
          clientName={clientName}
          clientEmail={clientEmail}
          experienceAvatar={experienceAvatar}
          cardFeePassthroughEnabled={platformFeeSetting?.card_fee_passthrough_enabled === true}
        />
      )}
      {isAdjustBookingPriceModalOpen && (
        <AdjustBookingPriceModal
          bookingUUID={bookingUUID}
          currentPrice={total_price}
          onOpen={isAdjustBookingPriceModalOpen}
          onClose={handleCloseAdjustBookingPriceModal}
          isMarketplaceBooking={isMarketplaceBooking}
        />
      )}
      <Modal
        open={isWithin24hModalOpen}
        onCancel={handleWithin24hCancel}
        closable={false}
        centered
        maskClosable={false}
        footer={null}
        width={400}
        className={styles.within24hModal}
      >
        <Button type="text" onClick={handleWithin24hCancel} className={styles.within24hCloseButton}>
          <CloseOutlined />
        </Button>
        <Flex justify="center" className="mb-m">
          <Flex justify="center" align="center" className={styles.within24hWarningIcon}>
            <img src={warningIcon} alt="warning" width="24" height="24" />
          </Flex>
        </Flex>
        <h2 className={styles.within24hTitle}>{t("booking.auto_collect_window_title", "Trip is within 24 hours")}</h2>
        <p className={styles.within24hDescription}>
          {pendingAutoCollectValue
            ? t(
                "booking.auto_collect_window_message_on",
                "This booking is within 24 hours. The client will be charged the remaining balance immediately. Do you want to proceed?"
              )
            : t(
                "booking.auto_collect_window_message_off",
                "This booking is within 24 hours. Turning off auto-collect will cancel the automatic charge of the remaining balance. Do you want to proceed?"
              )}
        </p>
        <Flex gap={8} className={styles.within24hFooter}>
          <Button color="primary" variant="outlined" onClick={handleWithin24hCancel} className={styles.within24hActionBtn}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button type="primary" onClick={handleWithin24hConfirm} className={styles.within24hActionBtn}>
            {t("common.proceed", "Proceed")}
          </Button>
        </Flex>
      </Modal>
    </>
  )
}

PaymentSummary.propTypes = {
  paymentSummary: PropTypes.shape({
    total: PropTypes.number,
    tax: PropTypes.number,
    adult: PropTypes.shape({
      count: PropTypes.number,
      price: PropTypes.number
    }),
    child: PropTypes.shape({
      count: PropTypes.number,
      price: PropTypes.number
    }),
    infant: PropTypes.shape({
      count: PropTypes.number,
      price: PropTypes.number
    }),
    remaining_balance: PropTypes.number,
    total_person_price: PropTypes.number,
    total_child_price: PropTypes.number,
    total_infant_price: PropTypes.number,
    child_allowed: PropTypes.bool,
    infants_allowed: PropTypes.bool,
    payment_option: PropTypes.string,
    apply_to_add_ons: PropTypes.bool,
    applicability_rules: PropTypes.shape({
      apply_to_add_ons: PropTypes.bool
    }),
    auto_collect_balance: PropTypes.bool
  }),
  customFees: PropTypes.array,
  bookingAddOns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      quantity: PropTypes.number,
      unit_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      photo_url: PropTypes.string
    })
  ),
  payment_status: PropTypes.string,
  hasPayments: PropTypes.bool,
  bookingUUID: PropTypes.string,
  bookingId: PropTypes.number,
  bookingStatus: PropTypes.string,
  pricing: PropTypes.object,
  bookingTitle: PropTypes.string,
  bookedOn: PropTypes.string,
  clientName: PropTypes.string,
  clientEmail: PropTypes.string,
  experienceAvatar: PropTypes.array,
  platformFeeSetting: PropTypes.object,
  bookingSource: PropTypes.string,
  bookedDate: PropTypes.string,
  startTime: PropTypes.string,
  is_chargeable: PropTypes.bool
}

export default PaymentSummary
